/**
 * Determines whether a set of scenarios results in an exposed asset.
 *
 * @param scenarioIds - Array of scenario IDs to check.
 * @param scenarios - Array of scenarios associated with the asset.
 * @returns 'true' if any scenario indicates exposure,
 *          'unknown' if any scenario is inconclusive,
 *          otherwise 'false'.
 */
import { ExposureStatus, ScenariosByAsset } from '../types'

export const areScenariosExposed = (
  scenarioIds: string[],
  scenarios: ScenariosByAsset[] | null,
): ExposureStatus => {
  if (!scenarios || scenarios.length === 0) return 'false'

  const matchingScenarios = scenarios.filter((s) => scenarioIds.includes(s.hazard_scenario))

  // If any relevant scenario is marked as exposed, return 'true'
  if (matchingScenarios.some((s) => s.is_exposed === 'true')) return 'true'

  // If any relevant scenario is unknown or based on calculated/no extent, return 'unknown'
  const hasUncertainExposure = matchingScenarios.some(
    (s) =>
      (s.hazard_extent_source === 'calculated' && s.is_exposed === 'false') ||
      s.is_exposed === 'unknown' ||
      s.hazard_extent_source === 'none',
  )
  if (hasUncertainExposure) return 'unknown'

  // Otherwise, asset is not exposed
  return 'false'
}
